/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/SubmittedDataset/SubmittedDataset.tsx.
 */

import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./SumittedDataset.css";
import axiosClient from "../../../../api/axiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import { useSearchParams } from "react-router-dom";
import DataTable, {
  Column,
} from "../../../../components/common/DataTable";
import OutlineButton from "../../../../components/common/OutlineButton";
// import StatCard from "../../../../components/common/StatCard";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import type { ReviewStatus } from "../../../../constants/reviewStatus";
import ContributorSubmittedDatasetDetailView from "./ContributorSubmittedDatasetDetailView";
import {
  CONTRIBUTOR_SUBMITTED_DATASETS_SECTION,
  CONTRIBUTOR_SUBMITTED_DATASET_ID_PARAM,
} from "../contributorDashboardNavigation";
interface SubmittedDatasetItem {
  submissionId: string;
  submissionCode: string;
  materialName: string | null;
  subMaterialName?: string | null;
  submittedDate?: string | null;
  submitted_date?: string | null;
  createdAt: string;
  reviewStatus?: ReviewStatus;
}

const PAGE_SIZE = 10;

const formatSubmittedDate = (dateString: string | null | undefined) => {
  if (dateString == null || dateString === "") return "—";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const displayDatasetSubmittedOn = (item: SubmittedDatasetItem) => {
  const raw = item.submittedDate ?? item.submitted_date;
  if (raw != null && raw !== "") return formatSubmittedDate(String(raw));
  return formatSubmittedDate(item.createdAt);
};

/* =======================
   Component
======================= */
const SubmittedDataset: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const submissionIdFromUrl = searchParams.get(CONTRIBUTOR_SUBMITTED_DATASET_ID_PARAM)?.trim() || null;
  const [detailSubmissionId, setDetailSubmissionId] = useState<string | null>(
    submissionIdFromUrl
  );

  useEffect(() => {
    setDetailSubmissionId(submissionIdFromUrl);
  }, [submissionIdFromUrl]);

  const [data, setData] = useState<SubmittedDatasetItem[]>([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Stats cards (Datasets / Materials Covered / Peer-reviewed Literature) — hidden per product request
  // const [stats, setStats] = useState<{
  //   datasetsCount: number;
  //   materialsCoveredCount: number;
  //   peerReviewedCount: number;
  // } | null>(null);
  // const [statsLoading, setStatsLoading] = useState<boolean>(false);

  const handleBackToList = useCallback(() => {
    setDetailSubmissionId(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("section", CONTRIBUTOR_SUBMITTED_DATASETS_SECTION);
        next.delete(CONTRIBUTOR_SUBMITTED_DATASET_ID_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const openDetail = useCallback(
    (submissionId: string) => {
      setDetailSubmissionId(submissionId);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("section", CONTRIBUTOR_SUBMITTED_DATASETS_SECTION);
          next.set(CONTRIBUTOR_SUBMITTED_DATASET_ID_PARAM, submissionId);
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );

  /* =======================
     Debounce search
  ======================= */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [search]);

  /* =======================
     Fetch data
  ======================= */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosClient.get(
        API_URLS.SUBMISSIONS.COMPLETED_REVIEW_STATUS,
        {
          params: {
            page,
            limit: PAGE_SIZE,
            search: debouncedSearch.trim() || undefined,
          },
        }
      );

      if (res.data?.success) {
        setData(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error("Error loading submitted datasets:", err);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* =======================
     Load dashboard stats (disabled while stat cards are commented out)
  ======================= */
  // useEffect(() => {
  //   (async () => {
  //     try {
  //       setStatsLoading(true);
  //       const res = await axiosClient.get("/contributor-dashboard/stats");
  //       if (res.data.success && res.data.data) {
  //         setStats(res.data.data);
  //       }
  //     } catch (err) {
  //       console.error("❌ Failed loading stats", err);
  //     } finally {
  //       setStatsLoading(false);
  //     }
  //   })();
  // }, []);

  /* =======================
     Helpers
  ======================= */
  // (helpers moved to module scope to keep hooks stable)

  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;

  // Column definitions
  const columns: Column<SubmittedDatasetItem>[] = useMemo(
    () => [
      {
        key: "submissionCode",
        label: "Submission Code",
      },
      {
        key: "materialName",
        label: "Material",
        render: (item: SubmittedDatasetItem) => item.materialName || "—",
      },
      {
        key: "subMaterialName",
        label: "Sub Material",
        render: (item: SubmittedDatasetItem) => item.subMaterialName || "—",
      },
      {
        key: "submittedDate",
        label: "Submitted on",
        render: (item: SubmittedDatasetItem) => displayDatasetSubmittedOn(item),
      },
    ],
    []
  );

  // Handle search change
  const handleSearchChange = (searchTerm: string) => {
    setPage(1);
    setSearch(searchTerm);
  };

  // Render actions
  const renderActions = (item: SubmittedDatasetItem) => (
    <button
      className="view-btn"
      type="button"
      onClick={() => openDetail(item.submissionId)}
    >
      View
    </button>
  );

  if (detailSubmissionId) {
    return (
      <ContributorSubmittedDatasetDetailView
        submissionId={detailSubmissionId}
        onBack={handleBackToList}
      />
    );
  }

  /* =======================
     Render
  ======================= */
  return (
    <div className="submitted-container">
      <AnalysisBanner
        title="Submitted Datasets"
      />

      {/* Stats (moved from Home) — commented out: Datasets / Materials Covered / Peer-reviewed Literature
      <div className="contributor-stats">
        <StatCard
          value={statsLoading ? "..." : stats ? `${stats.datasetsCount}` : "0"}
          label="Datasets"
        />
        <StatCard
          value={
            statsLoading ? "..." : stats ? `${stats.materialsCoveredCount}` : "0"
          }
          label="Materials Covered"
        />
        <StatCard
          value={
            statsLoading ? "..." : stats ? `${stats.peerReviewedCount}` : "0"
          }
          label="Peer-reviewed Literature"
        />
      </div>
      */}

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        title="Submitted Datasets"
        searchPlaceholder="Search by submission code or material"
        onSearch={handleSearchChange}
        searchValue={search}
        pagination={false}
        renderActions={renderActions}
        emptyMessage="No datasets found for the selected criteria."
      />

      {/* Server-side Pagination */}
      {!loading && total > 0 && (
        <div className="pagination">
          <div className="pagination-info">
            <span>
              Showing {((page - 1) * PAGE_SIZE) + 1} to {Math.min(page * PAGE_SIZE, total)} of {total} entries
            </span>
          </div>

          <div className="pagination-controls">
            <OutlineButton
              label="Previous"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              type="button"
            />

            {/* Page Numbers */}
            <div className="pagination-numbers">
              {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 7) {
                  pageNum = i + 1;
                } else if (page <= 4) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 3) {
                  pageNum = totalPages - 6 + i;
                } else {
                  pageNum = page - 3 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`pagination-number ${page === pageNum ? "active" : ""}`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <OutlineButton
              label="Next"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              type="button"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/** React component — Submitted Dataset. */
export default SubmittedDataset;